import { closedTicketByID, rollbackTicketByID } from '@/modules/Ticket/services'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Collapse, Typogaphy } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import TicketRemedialMeasures from '@/modules/Ticket/components/Forms/TicketRemedialMeasures'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import CheckIcons from '@/icons/CheckIcons'
import { useState } from 'react'
import TicketContent from '../../../Forms/TicketContent'
import HelpIcons from '@/icons/HelpIcons'
import { useAppStore } from '@/stores/useAppStore'
import closedHelpContent from '@/modules/Ticket/help/ticket-closed-help.md'
import rollbackHelpContent from '@/modules/Ticket/help/ticket-rollback-help.md'
import UndoIcons from '@/icons/UndoIcons'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import TicketAffected from '../../../Forms/TicketAffected'
import TicketInternal from '../../../Forms/TicketInternal'
import TicketRootCause from '../../../Forms/TicketRootCause'
import TicketCAPA from '../../../Forms/TicketCAPA'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Link } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import MailIcons from '@/icons/MailIcons'

interface CloseTicketProps {
    ticket: any
}
interface CloseTicketFormValues {
    ticket_content: { text: string; attachments: any[] }
    ticket_remedial_measures: { text: string; attachments: any[] }
    ticket_affected: { text: string; attachments: any[] }
    ticket_root_cause: { text: string; attachments: any[] }
    ticket_avoid: { text: string; attachments: any[] }
    ticket_time_release: any
    ticket_internal_root_cause: { text: string; attachments: any[] }
    ticket_internal_remedial_measures: { text: string; attachments: any[] }
    ticket_internal_avoid: { text: string; attachments: any[] }
}
const CloseTicket: React.FC<CloseTicketProps> = ({ ticket }) => {
    const { t } = useTranslation()
    const { setIsShowHelp, setHelpContent, isShowHelp } = useAppStore()
    const isManager = true
    const isServiceTicket = ticket?.ticket_type_group === 'service'

    const queryClient = useQueryClient()
    const { refetchAll: refetchLogs } = useDataGridRefetch({
        reactQueryKey: `ticket-logs-${ticket?.ticket_id}`,
    })
    const { control } = useForm<CloseTicketFormValues>({
        defaultValues: {
            ticket_content: ticket?.ticket_content || {
                text: '',
                attachments: [],
            },
            ticket_remedial_measures: ticket?.ticket_remedial_measures || {
                text: '',
                attachments: [],
            },
            ticket_affected: ticket?.ticket_affected || {
                text: '',
                attachments: [],
            },
            ticket_root_cause: ticket?.ticket_root_cause || {
                text: '',
                attachments: [],
            },
            ticket_avoid: ticket?.ticket_avoid || {
                text: '',
                attachments: [],
            },
            ticket_time_release: dayjs(),
            ticket_internal_root_cause: ticket?.ticket_internal_root_cause || {
                text: '',
                attachments: [],
            },
            ticket_internal_remedial_measures:
                ticket?.ticket_internal_remedial_measures || {
                    text: '',
                    attachments: [],
                },
            ticket_internal_avoid: ticket?.ticket_internal_avoid || {
                text: '',
                attachments: [],
            },
        },
    })

    const { mutateAsync: rollbackTicketMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: rollbackTicketByID },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('updated-success'),
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [
                            QUERY_KEYS.TICKET_DETAIL,
                            String(ticket.ticket_id),
                        ],
                    })
                    refetchLogs()
                },
            }
        )
    const { mutateAsync: closedTicketMutation, isPending: isClosed } =
        useMutationWithHandler(
            { mutationFn: closedTicketByID },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('updated-success'),
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [
                            QUERY_KEYS.TICKET_DETAIL,
                            String(ticket.ticket_id),
                        ],
                    })
                    refetchLogs()
                },
            }
        )
    const [rollbackReason, setRollbackReason] = useState('')
    const [onRollback, setOnRollback] = useState(false)
    const rollbackSubmit = async () => {
        if (!rollbackReason) {
            return toast.error('Vui lòng nhập lý do yêu cầu thực hiện lại.')
        }
        await rollbackTicketMutation({
            ticket_id: ticket?.ticket_id,
            content: rollbackReason,
        })
    }

    // Check if this is a service creating ticket that can send service notification
    const canSendServiceNotification =
        isServiceTicket &&
        (ticket?.ticket_type_key === 'service_creating' ||
            ticket?.ticket_type_key === 'service_creating_poc') &&
        ticket?.service_id

    const isIncidentTicket = ticket?.ticket_type_id === 2
    const isTicketCompleted = ticket?.ticket_status === 'completed'
    const isTicketClosed = ticket?.ticket_status === 'closed'
    const showPermissionNotice = !isManager && !isTicketClosed

    const handleHelpIconMouseDown =
        (content: string) => (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            setIsShowHelp(!isShowHelp)
            setHelpContent(content)
        }

    const renderReportDetails = () => {
        if (isIncidentTicket) {
            return (
                <>
                    <TicketAffected control={control} viewOnly={true} />
                    <div className="mt-4">
                        <Typogaphy className="font-medium text-primary">
                            {t('ticket-internal-report')}
                        </Typogaphy>
                        <TicketInternal control={control} viewOnly={true} />
                    </div>
                    <div className="mt-4">
                        <Typogaphy className="font-medium text-primary">
                            {t('ticket-external-report')}
                        </Typogaphy>
                        <TicketRootCause control={control} viewOnly={true} />
                        <TicketCAPA
                            control={control}
                            viewOnly={true}
                            showAllFields={
                                ticket?.ticket_type_group === 'error_service'
                            }
                        />
                    </div>
                </>
            )
        }
        return (
            <div className="mt-4">
                <TicketRemedialMeasures
                    control={control}
                    ticket={ticket}
                    viewOnly={true}
                />
            </div>
        )
    }

    const renderActionButtons = () => {
        if (showPermissionNotice) {
            return (
                <div className="items-center p-4 border rounded-sm mt-4 mb-4">
                    <Typogaphy size="small">
                        {t('ticket-permission-closed')}
                    </Typogaphy>
                    <Typogaphy size="small">{t('ticket-permission')}</Typogaphy>
                </div>
            )
        }
        if (!isTicketCompleted) return null
        return (
            <div className="flex justify-end mt-2 gap-2 mb-2">
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UndoIcons />}
                    endIcon={
                        <div
                            onMouseDown={handleHelpIconMouseDown(
                                rollbackHelpContent
                            )}
                        >
                            <HelpIcons />
                        </div>
                    }
                    onClick={
                        onRollback ? rollbackSubmit : () => setOnRollback(true)
                    }
                    loading={isPending}
                    disabled={isPending}
                >
                    {onRollback ? 'Rollback' : 'Back To Inprogress'}
                </Button>

                {!onRollback && (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckIcons />}
                        endIcon={
                            <div
                                onMouseDown={handleHelpIconMouseDown(
                                    closedHelpContent
                                )}
                            >
                                <HelpIcons />
                            </div>
                        }
                        onClick={async () =>
                            await closedTicketMutation({
                                ticket_id: ticket?.ticket_id,
                            })
                        }
                        loading={isClosed}
                        disabled={isClosed}
                    >
                        Closed
                    </Button>
                )}
            </div>
        )
    }

    // Service ticket - simple status display
    if (isServiceTicket) {
        return (
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                    {t('ticket-service-workflow-completed')}
                </span>
                {canSendServiceNotification && (
                    <Link
                        to={`${generatePathDatacentral(paths.serviceDetail, { id: ticket.service_id })}?tab=service-notification&action=create-notification&ticket_id=${ticket.ticket_id}`}
                        target="_blank"
                    >
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<MailIcons />}
                        >
                            {t('send-service-information')}
                        </Button>
                    </Link>
                )}
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-2">
            <Typogaphy size="medium" className="text-blue-500 font-medium">
                {t('ticket-incident-information')}
            </Typogaphy>
            <TicketContent control={control} viewOnly={true} ticket={ticket} />
            {renderReportDetails()}
            <Collapse open={onRollback}>
                <div className="mt-4">
                    <Typogaphy size="small" className=" mb-2">
                        {t('ticket-reexecution')}
                    </Typogaphy>
                    <TextEditorCustomize
                        value={{ text: rollbackReason, attachments: [] }}
                        onChange={(newText) => setRollbackReason(newText?.text)}
                    />
                </div>
            </Collapse>
            {renderActionButtons()}

            {isTicketClosed && (
                <Typogaphy size="small" className="mb-4 text-success">
                    {t('ticket-final-closed')}
                </Typogaphy>
            )}
        </div>
    )
}

export default CloseTicket
